import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import InvoiceDiscrepancyPanel from './InvoiceDiscrepancyPanel';
import RaiseAdjustmentNoteDialog from './RaiseAdjustmentNoteDialog';
import { discrepancyForDisplay } from './invoiceDiscrepancyUtils';

/**
 * PGSS-5306 — Staff Invoices entry point after Invoiced.
 *
 * Update Invoice is DS/DP-only and edits tax/shipping. Confirm Invoice is pre-issue.
 * This dialog is the discrepancy panel only (Accept / Flag / Raise) — no rewrite of the
 * issued invoice. When there is no active mismatch, finance can still raise a note.
 */
function ReviewInvoiceDiscrepancy({ isDashboard }) {
  const orderDialog = useSelector(({ mainDashboardApp, eCommerceApp }) => {
    if (isDashboard) {
      return mainDashboardApp?.widgets?.orderDialog;
    }
    return eCommerceApp?.order?.orderDialog;
  });
  const invoiceId = orderDialog?.data?.id;
  const [discrepancy, setDiscrepancy] = useState(
    discrepancyForDisplay(orderDialog?.data?.discrepancy) || null
  );
  const [raiseOpen, setRaiseOpen] = useState(false);
  // The list that opened this dialog (Staff Invoices, or the dashboard widget). A new note changes
  // the invoice's balance and notes, so the list is reloaded -- otherwise it only showed after a
  // manual refresh.
  const refreshList = orderDialog?.param?.loadOrdersFun;

  const refreshAfterSync = (payload) => {
    if (payload && Object.prototype.hasOwnProperty.call(payload, 'discrepancy')) {
      setDiscrepancy(discrepancyForDisplay(payload.discrepancy));
    }
  };

  const hasPanel =
    discrepancy
    && invoiceId
    && (discrepancy.has_discrepancy || discrepancy.latest_decision);

  return (
    <>
      {hasPanel ? (
        <>
          <InvoiceDiscrepancyPanel
            invoiceId={invoiceId}
            discrepancy={discrepancy}
            onUpdated={setDiscrepancy}
            onSynced={refreshAfterSync}
            onNoteRaised={refreshList}
            compact
          />
          <Divider className="mb-16 mt-8" />
        </>
      ) : (
        <Typography variant="body2" color="textSecondary" className="mb-16">
          This invoice matches its order. You can still raise a credit or debit note if finance
          needs to restate what is owed — the issued invoice is not rewritten.
        </Typography>
      )}
      {!hasPanel && invoiceId ? (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => setRaiseOpen(true)}
          >
            Raise credit/debit note
          </Button>
        </Stack>
      ) : null}
      {raiseOpen && invoiceId ? (
        <RaiseAdjustmentNoteDialog
          open={raiseOpen}
          onClose={() => setRaiseOpen(false)}
          invoiceId={invoiceId}
          onCreated={refreshList}
        />
      ) : null}
    </>
  );
}

export default ReviewInvoiceDiscrepancy;
